import { useCallback } from "react";
import { useStore } from "react-redux";
import { AddLineInput } from "../../../utils/cart/cartLineTypes";
import { LoyaltyItemOffer } from "../../../utils/LoyaltyOfferClass/LoyaltyItemOffer";
import { createLoyaltyOffer } from "../../../utils/LoyaltyOfferClass/LoyaltyOfferCreator/loyaltyOfferCreator";
import {
  LoyaltyApplicationLevel,
  LoyaltyOfferType,
} from "../../../utils/loyaltyTypes";
import { SubItemType } from "../../../utils/types";
import { useAppDispatch } from "../../hooks/useReduxHooks";
import { useModalHook } from "../../hooks/useModal";
import { AppDispatch, RootState } from "../app/store";
import { selectIsPayWithPointsApplied } from "../selectors/payWithPointsSelectors";
import {
  applyLoyaltyItemOffer,
  redeemLoyaltyOffer,
} from "../slices/cartLoyaltyV2Slice";

type ApplyLoyaltyItemOfferFlowParams = {
  offer: LoyaltyOfferType;
  menuSubItems: SubItemType[];
  customisedLines?: AddLineInput[];
  /** When false, skip redeem (e.g. promo code already redeemed). Default true. */
  redeem?: boolean;
  /**
   * When true, redeem even though a points discount is applied. Set by the chooser
   * itself and by later steps of a redemption the chooser already asked about.
   */
  skipPointsConflictCheck?: boolean;
};

export function getMenuSubItemsForOffer(
  offer: LoyaltyOfferType,
  subItemLookup: Record<string, SubItemType | undefined>
): SubItemType[] {
  return (offer.itemLevelConfig?.targetEntities ?? [])
    .map((e) => subItemLookup[e.entityId])
    .filter((subItem): subItem is SubItemType => Boolean(subItem));
}

export function shouldDeferRedeemUntilItemSelected(
  offer: LoyaltyOfferType,
  menuSubItems: SubItemType[],
  customisedLines?: AddLineInput[]
): boolean {
  if (customisedLines?.length) {
    return false;
  }

  const loyaltyOffer = createLoyaltyOffer(offer);

  return (
    loyaltyOffer instanceof LoyaltyItemOffer &&
    loyaltyOffer.isItemFromListOffer() &&
    menuSubItems.length > 1
  );
}

/** Redeem + apply item offer; reads fresh cartVersion after redeem. */
export function dispatchApplyLoyaltyItemOfferFlow(
  dispatch: AppDispatch,
  getState: () => RootState,
  params: ApplyLoyaltyItemOfferFlowParams
): void {
  const { offer, menuSubItems, customisedLines, redeem = true } = params;
  const deferRedeem = shouldDeferRedeemUntilItemSelected(
    offer,
    menuSubItems,
    customisedLines
  );

  if (redeem && !deferRedeem) {
    dispatch(redeemLoyaltyOffer(offer));
  }
  if (offer.applicationLevel !== LoyaltyApplicationLevel.ITEM) {
    return;
  }

  const cartVersion = getState().cartLoyaltyV2Reducer.latestCartVersion;
  dispatch(
    applyLoyaltyItemOffer({
      offer,
      menuSubItems,
      customisedLines,
      cartVersion,
    })
  );
}

export function useApplyLoyaltyItemOfferFlow() {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const { handleModal } = useModalHook();

  return useCallback(
    (params: ApplyLoyaltyItemOfferFlowParams) => {
      // ----------------------redeemLoyaltyOffer drops an applied points discount, so the chooser has to ask before it runs-----------------------
      if (
        !params.skipPointsConflictCheck &&
        params.redeem !== false &&
        selectIsPayWithPointsApplied(store.getState())
      ) {
        handleModal(true, "LoyaltyOffersPopup/LoyaltyOffersPopup", {
          componentProps: {
            pendingOffer: params.offer,
            pendingOfferSubItems: params.menuSubItems,
          },
        });

        return;
      }

      dispatchApplyLoyaltyItemOfferFlow(dispatch, store.getState, params);
    },
    [dispatch, store, handleModal]
  );
}
